/**
 * Design tokens — colors
 * Semantic colors are fixed; brand colors use Boundless as default (tenant overrides apply at runtime).
 */

/** Fixed colors for UI chrome — never overridden by tenant branding */
export const semantic = {
  text: '#202020',
  textMuted: '#838383',
  textInverse: '#FFFFFF',
  background: '#FFFFFF',
  backgroundSecondary: '#F2F2F2',
  surface: '#FFFFFF',
  border: '#E5E5E5',
  borderMuted: '#D9D9D9',
  /**
   * Strongest neutral in the border ramp (`border` #E5E5E5 → `borderMuted`
   * #D9D9D9 → `borderStrong` #C0C0C0). Both web apps bind `--border` and
   * `--input` to this, NOT to `border` — mapping them to `border` would lighten
   * every input outline in both apps (BL-998).
   */
  borderStrong: '#C0C0C0',
  muted: '#D9D9D9',
  error: '#DC2626',
  success: '#10B981',
  warning: '#F59E0B',
  info: '#1976D2',
  overlay: 'rgba(0, 0, 0, 0.5)',
  /**
   * Lot bid history — status colors used by `BidRow` (per-row tint) and
   * `StatusBadge` (the inline banner above the user's row).
   *
   *   winning : user's bid is currently top → green family
   *   outbid  : a higher bid exists → amber/gold family
   *   won     : auction closed and user was top → deeper teal-green
   *
   * `text` is for the amount + banner copy, `dot` is for the pulsing
   * "You" indicator, `rowBg` is the lighter row tint, and `bg` is the
   * darker banner background.
   */
  bidStatus: {
    winning: {
      text: '#027A48',
      bg: '#DAFBE8',
      rowBg: '#F6FEF9',
      dot: '#009965',
      dotRing: 'rgba(0, 153, 101, 0.2)',
    },
    won: {
      text: '#027A48',
      // Same green as `winning.bg` — once the lot closes, the visual
      // doesn't change, only the banner copy ("You Won!" vs "You're winning").
      bg: '#DAFBE8',
    },
    outbid: {
      text: '#AD5F25',
      bg: '#FFF5CC',
      rowBg: '#FFFDF5',
      dot: '#AD5F25',
      dotRing: 'rgba(173, 95, 37, 0.2)',
    },
  },
} as const;

/**
 * The canonical Auctogon brand palette — the single source of truth for brand
 * values across both web apps (via the generated CSS vars, BL-998) and React
 * Native. Tenant branding overrides `primary` / `secondary` / `accent` at
 * runtime; the rest is fixed chrome.
 *
 * Ratified in [ADR 030](../../../../docs/adrs/030_DESIGN_SYSTEM_FOUNDATION.md)
 * § 1a. (Previously commented "Boundless palette" — a retired corporate
 * identity; Auctogon Holdings, Inc. is the operating entity.)
 */
export const brand = {
  primary: '#09514d',
  primaryForeground: '#45d784',
  /** Pressed/hover state for `primary`. `#0a635e` is drift against this, not a role. */
  primaryHover: '#0a6159',
  secondary: '#45d784',
  secondaryForeground: '#09514d',
  accent: '#45d784',
  surface: '#FFFFFF',
  /** Pale brand-green wash for surfaces — emitted as `--brand-surface`. */
  surfaceTint: '#f0f9f4',
  /**
   * High-emphasis text and hover — the one role the product palette genuinely
   * lacked, adopted from marketing (ADR 030 § 1a). Deferred out of the PR that
   * built the pipeline so it wouldn't ship as an unused export; it lands here
   * with the `--brand-*` rename that gives it a CSS var and a Tailwind token.
   */
  ink: '#012d20',
} as const;

/** Core schema uses: primary, secondary, accent, surface, text */
export interface BrandColors {
  primary: string;
  secondary: string;
  accent: string;
  surface: string;
  text: string;
}

/** Default brand colors for core/tenant-branding when no tenant is found */
export function getDefaultBrandColors(): BrandColors {
  return {
    primary: brand.primary,
    secondary: brand.secondary,
    accent: brand.accent,
    surface: brand.surface,
    text: semantic.text,
  };
}

/** Combined palette: semantic + brand (backward compatibility during migration) */
export const colors = {
  ...semantic,
  ...brand,
  // Legacy aliases
  primary: brand.primary,
  primaryLight: '#F0F9F4',
  text: semantic.text,
  textMuted: semantic.textMuted,
  background: semantic.background,
  border: semantic.border,
  error: semantic.error,
  success: semantic.success,
  warning: semantic.warning,
} as const;
